import { useEffect, useState, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Button, Table, Tag, Typography, Input, Flex, Space, Tooltip } from 'antd';
import { convertDateTimeRangeToLocalText, buildAuthDictMaps, formatOfficeAndPost } from '@utils/utils';
import html2canvas from 'html2canvas';
import { laborFeeCostBearers, laborFeePaymentMethods } from '@utils/constants';
import { conferenceOnceApi } from '@services/Conference';
import { fetchLaborFeeExaminationList, fetchLaborFeeExaminationResult, fetchLaborFeeApprovalProcessList } from '@services/Finance/FinanceLaborFee.service.js';
import './FinanceLaborFeeProject.less';
import dayjs from 'dayjs';
import { localStorageGet } from '@core/cookie';
import { authdictionaryApi } from '@services/Conference/sign';
import { isEmpty } from 'lodash';
import ExpertInfoCard from '@/components/common/ExpertInfoCard';
import FinanceApprovalDetailModal from './components/FinanceApprovalDetailModal';
import PageHeader from '@components/PageHeader/PageHeader';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { TABLE_PAGE_SIZE_OPTIONS } from '@/utils/property';
const { Text } = Typography;

export default function FinanceLaborFeeProject() {
    const params = useParams();

    // 接收项目类型-项目ID路由参数
    const projectCodeAndId = params.project || '';
    let projectCode = '';
    let projectId = 0;
    if (!!projectCodeAndId && /[a-z]+-\d+/i.test(projectCodeAndId)) {
        [projectCode, projectId] = projectCodeAndId.split('-');
        projectCode = projectCode.toUpperCase() || '';
        projectId = parseInt(projectId, 10) || 0;
    }

    const [conference, setConference] = useState(null);

    // 获取当前登录用户手机号
    const currentUserPhone = useMemo(() => {
        const infos = localStorageGet('infos') ? JSON.parse(localStorageGet('infos')) : {};
        return infos.phone || '';
    }, []);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [statisticalAmount, setStatisticalAmount] = useState({});

    const [openPaymentRecord, setOpenPaymentRecord] = useState(false);
    const [paymentRecord, setPaymentRecord] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const [approvalProcessList, setApprovalProcessList] = useState([]); // 审批流程列表

    // 认证字典数据
    const [authDict, setAuthDict] = useState([]);

    // fixed-scroll 三件套：表格高度自适应 + 横向滚动守卫 + 纵向阴影
    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, { threshold: 6, edgeHoldMs: 220, watchValue: scrollContainerVersion });
    useVerticalScrollShadow(tableScrollContainerRef, { threshold: 2, watchValue: scrollContainerVersion });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;
        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };
        resolveScrollContainer();
        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    // 计算当前审批节点和按钮显示逻辑
    const approvalButtonConfig = useMemo(() => {
        const defaultConfig = {
            showApprovalBtn: false,
            showApprovedBtn: false,
            showDetailBtn: true,
            showAgreeBtn: false,
            showDisagreeBtn: false,
        };

        // 查找财务经理和财务总监审批节点
        const financeManagerNode = approvalProcessList.find(node => node.nodeId === 5);
        const financeDirectorNode = approvalProcessList.find(node => node.nodeId === 6);

        // 如果两个节点都不存在，只展示审批详情按钮
        if (!financeManagerNode && !financeDirectorNode) return defaultConfig;

        // 判断节点状态和审批人身份的辅助函数
        const checkNode = node => {
            if (!node || !currentUserPhone) return { isCurrentNode: false, isApprover: false, isApproved: false };
            return {
                isCurrentNode: node.approvalStatus === 1, // 待审核
                isApprover: node.approvalBy === currentUserPhone,
                isApproved: node.approvalStatus === 2, // 已同意
            };
        };

        const managerStatus = checkNode(financeManagerNode);
        const directorStatus = checkNode(financeDirectorNode);

        // 优先处理当前节点逻辑（同一人担任多角色时，优先展示当前节点）
        // 情况1：当前节点 + 当前审批人 → 展示劳务审批按钮，弹窗底部全部按钮
        if ((managerStatus.isCurrentNode && managerStatus.isApprover) || (directorStatus.isCurrentNode && directorStatus.isApprover)) {
            return {
                ...defaultConfig,
                showApprovalBtn: true,
                showAgreeBtn: true,
                showDisagreeBtn: true,
            };
        }

        // 情况2：只满足当前节点，不满足当前审批人 → 只展示审批详情按钮，弹窗底部无按钮
        if (managerStatus.isCurrentNode || directorStatus.isCurrentNode) {
            return defaultConfig;
        }

        // 情况3：不满足当前节点，但满足当前审批人且已同意 → 只展示审批详情按钮，弹窗底部只展示不同意按钮（支持撤回）
        if ((managerStatus.isApprover && managerStatus.isApproved) || (directorStatus.isApprover && directorStatus.isApproved)) {
            return {
                ...defaultConfig,
                showDisagreeBtn: true,
            };
        }

        // 其他情况：只展示审批详情按钮，弹窗底部无按钮
        return defaultConfig;
    }, [approvalProcessList, currentUserPhone]);

    useEffect(() => {
        document.title = '含税劳务费总额审批-财务中心';
    }, []);

    // 请求认证字典数据
    const requestAuthDict = async () => {
        const res = await authdictionaryApi();
        if (!isEmpty(res?.options)) {
            const authDictData = res.options;
            authDictData
                && Array.isArray(authDictData)
                && authDictData.forEach(authType => {
                    authType.labels
                        && Array.isArray(authType.labels)
                        && authType.labels.forEach(label => {
                            label.options
                                && Array.isArray(label.options)
                                && label.options.forEach(l => {
                                    if (isEmpty(l.options)) {
                                        delete l.options;
                                    }
                                });
                        });
                });
            setAuthDict(authDictData);
        }
    };

    // 是否拥有财务劳务费管理员权限
    // const isLaborFeeManager = useMemo(() => {
    //     const roleList = localStorageGet('roleList') ? JSON.parse(localStorageGet('roleList')) : [];
    //     if (roleList && roleList.length) {
    //         return roleList.some(item => item.name === '财务劳务费管理员');
    //     }
    //     return false;
    // }, []);

    // 根据认证字典构建映射
    const dictMaps = useMemo(() => buildAuthDictMaps(authDict), [authDict]);

    const columns = [
        { title: '基本信息', width: 200, dataIndex: 'memUsername', fixed: 'left', render: (_, record) => <ExpertInfoCard expert={record} /> },
        {
            title: '单位及职务',
            dataIndex: 'unitPost',
            width: 180,
            render: (_, row) => {
                if (row.workExpList && row.workExpList.length) {
                    return (
                        <>
                            {row.workExpList.map((item, index) => {
                                const officePostDisplay = formatOfficeAndPost(item, dictMaps);
                                const unitName = item.unitName || '';
                                const fullDisplay = [unitName, officePostDisplay].filter(Boolean).join('');
                                const displayIndex = index + 1;

                                return (
                                    <span key={`w_${item.id}`}>
                                        {row.workExpList.length > 1 && `${displayIndex}. `}
                                        {fullDisplay || ''}
                                    </span>
                                );
                            })}
                        </>
                    );
                } else {
                    return '—';
                }
            },
        },
        {
            title: '实际日程',
            dataIndex: 'agendasList',
            width: 400,
            render: (_, record) => {
                if (record.agendasList && record.agendasList.length) {
                    return (
                        <>
                            <ul className="task-list">
                                {record.agendasList.map((item, idx) => (
                                    <li key={`agenda_${item.id}`}>
                                        <span style={{ marginRight: 4 }}>{`${item.conferenceHallName} / ${item.title}`}</span>
                                        <Tag color="blue">{item?.taskName}</Tag>
                                        <br />
                                        <span className="task-time">{convertDateTimeRangeToLocalText(item.startTime, item.endTime, 0)}</span>
                                    </li>
                                ))}
                            </ul>
                            <div>{!!conference?.agendaConfirmContactsName ? record.agendaConfirmed ? <Text type="success">已确认</Text> : <Text type="secondary">未反馈</Text> : <Text type="secondary">&ndash;</Text>}</div>
                        </>
                    );
                } else {
                    return (
                        <>
                            <div>
                                <Text type="secondary">未安排任务</Text>
                            </div>
                            <div>{!!conference?.agendaConfirmContactsName ? record.agendaConfirmed ? <Text type="success">已确认</Text> : <Text type="secondary">未反馈</Text> : <Text type="secondary">&ndash;</Text>}</div>
                        </>
                    );
                }
            },
        },
        {
            title: '实发劳务费(元)',
            dataIndex: 'laborFeeAmount',
            width: 130,
            editable: !!conference?.laborFeeConfirmedAt ? false : true,
            render: (_, record) => (record.laborFeeAmount !== null ? <span>&yen;{`${formatCurrency(record.laborFeeAmount)}`}</span> : <Text type="secondary">&ndash;</Text>),
        },
        {
            title: '付款方式',
            dataIndex: 'paymentMethod',
            width: 150,
            editable: true,
            align: 'center',
            render: (_, record) => {
                const cur = laborFeePaymentMethods.find(m => m.value === record.paymentMethod);
                if (cur) {
                    if (cur.value === 3) {
                        return <Text type="warning">{cur?.label}</Text>;
                    }
                    return cur?.label;
                }
                return <Text type="secondary">&ndash;</Text>;
            },
        },
        {
            title: '劳务费支付主体',
            dataIndex: 'costBearer',
            width: 140,
            editable: true,
            render: (_, record) => laborFeeCostBearers.find(b => b.value === record.costBearer)?.label || <Text type="secondary">&ndash;</Text>,
        },
        {
            title: '相关税费(元)',
            dataIndex: 'individualTaxAmount',
            width: 120,
            align: 'right',
            render: (_, record) => {
                // if (!!record.laborFeeAmount && !!record.paymentMethod) {
                return !!record.individualTaxAmount ? <span>&yen;{`${formatCurrency(record.individualTaxAmount)}`}</span> : <span>&yen;0.00</span>;
                // }
                // return <Text type="secondary">&ndash;</Text>
            },
        },
        {
            title: '含税劳务费(元)',
            dataIndex: 'sumOfLaborFeeCast',
            width: 130,
            align: 'right',
            render: (_, record) => {
                // if (!!record.laborFeeAmount) {
                const laborFeeAmount = parseFloat(record.laborFeeAmount) || 0.0;
                const individualTaxAmount = parseFloat(record.individualTaxAmount) || 0.0;

                return <span>&yen;{`${formatCurrency(laborFeeAmount + individualTaxAmount)}`}</span>;
                // }
                // return <Text type="secondary">&ndash;</Text>;
            },
        },
        {
            title: '收款信息',
            dataIndex: 'bankName',
            width: 250,
            render: (_, record) => (
                <>
                    {(record.idcardType === 1 || !record.idcardType) && (
                        <>
                            <span>身份证号码：{record.idCard || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>银行账号：{record.bankCardNo || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>开户银行：{record.bankName || <Text type="secondary">&ndash;</Text>}</span>
                            <div>{!!conference?.agendaConfirmContactsName ? record.laborInfoConfirmed ? <Text type="success">已确认</Text> : <Text type="secondary">未反馈</Text> : <Text type="secondary">&ndash;</Text>}</div>
                        </>
                    )}
                    {record.idcardType > 1 && (
                        <>
                            <span>证件类型：{getIdcardType(record.idcardType) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>证件号：{record.idCard || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>国籍：{record.nationality || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生日期：{(!!record.birthday && dayjs(record.birthday).format('YYYY-MM-DD')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生地：{record.birthPlace || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>性别：{(record.memGender !== null && (Number(record.memGender) === 1 ? '男' : '女')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>银行账号：{record.bankCardNo || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>开户银行：{record.bankName || <Text type="secondary">&ndash;</Text>}</span>
                            <div>{!!conference?.agendaConfirmContactsName ? record.laborInfoConfirmed ? <Text type="success">已确认</Text> : <Text type="secondary">未反馈</Text> : <Text type="secondary">&ndash;</Text>}</div>
                        </>
                    )}
                </>
            ),
        },
    ];

    const modalColumns = [
        { title: '专家姓名', width: 100, dataIndex: 'memUsername', fixed: 'left' },
        { title: '手机号', dataIndex: 'memMobile', width: 120 },
        {
            title: '单位任职',
            dataIndex: 'unitPost',
            width: 180,
            render: (_, row) => {
                if (!!row.workExpList && !!row.workExpList.length) {
                    return (
                        <>
                            {row.workExpList.map((item, idx) => {
                                return (
                                    <div key={`w_${item.id}`}>
                                        {row.workExpList.length > 1 && <span>{idx + 1}. </span>}
                                        {!!item.unitName && <span>{item.unitName}</span>}
                                        {!!item.unitOffice && <span>{item.unitOffice}</span>}
                                        {!!item.unitPost && <span>{item.unitPost}</span>}
                                    </div>
                                );
                            })}
                        </>
                    );
                } else {
                    return '—';
                }
            },
        },
        {
            title: '劳务费',
            width: 380,
            render: (_, record) => {
                const laborFeeAmount = parseFloat(record.laborFeeAmount) || 0.0;
                const laborFeeAmountAdjust = record.laborFeeAmountAdjust === null ? laborFeeAmount : parseFloat(record.laborFeeAmountAdjust);
                const individualTaxAmount = parseFloat(record.individualTaxAmount) || 0.0;
                const individualTaxAmountAdjust = parseFloat(record.individualTaxAmountAdjust) || 0.0;
                const totalAmount = laborFeeAmount + individualTaxAmount;
                const totalAmountAdjust = laborFeeAmountAdjust + individualTaxAmountAdjust;

                const hasLaborFeeChange = laborFeeAmount !== laborFeeAmountAdjust;
                const hasTaxChange = individualTaxAmount !== individualTaxAmountAdjust;

                const paymentMethodName = laborFeePaymentMethods.find(m => m.value === record.paymentMethod)?.label || <Text type="secondary">&ndash;</Text>;
                const paymentMethodAdjustName = record.paymentMethod !== record.paymentMethodAdjust ? laborFeePaymentMethods.find(m => m.value === record.paymentMethodAdjust)?.label || <>&ndash;</> : null;

                const costBearerName = laborFeeCostBearers.find(m => m.value === record.costBearer)?.label || <Text type="secondary">&ndash;</Text>;
                const costBearerAdjustName = record.costBearer !== record.costBearerAdjust ? laborFeeCostBearers.find(m => m.value === record.costBearerAdjust)?.label || <>&ndash;</> : null;

                return (
                    <div>
                        <div>
                            实发劳务费：&yen;{formatCurrency(laborFeeAmount)}
                            {hasLaborFeeChange && <Text type="danger"> 变更后 &yen;{formatCurrency(laborFeeAmountAdjust)}</Text>}
                        </div>
                        <div>
                            个人所得税：&yen;{formatCurrency(individualTaxAmount)}
                            {hasTaxChange && <Text type="danger"> 变更后 &yen;{formatCurrency(individualTaxAmountAdjust)}</Text>}
                        </div>
                        <div>
                            含税劳务费：&yen;{formatCurrency(totalAmount)}
                            {(hasLaborFeeChange || hasTaxChange) && <Text type="danger"> 变更后 &yen;{formatCurrency(totalAmountAdjust)}</Text>}
                        </div>
                        <div>
                            付款方式：{paymentMethodName}
                            {paymentMethodAdjustName && <Text type="danger"> 变更后 {paymentMethodAdjustName}</Text>}
                        </div>
                        <div>
                            费用承担主体：{costBearerName}
                            {costBearerAdjustName && <Text type="danger"> 变更后 {costBearerAdjustName}</Text>}
                        </div>
                    </div>
                );
            },
        },
        {
            title: '收款信息',
            dataIndex: 'bankName',
            width: 250,
            render: (_, record) => (
                <>
                    {(record.idcardType === 1 || !record.idcardType) && (
                        <>
                            <span>身份证号码：{record.idCard || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>银行账号：{record.bankCardNo || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>开户银行：{record.bankName || <Text type="secondary">&ndash;</Text>}</span>
                        </>
                    )}
                    {record.idcardType > 1 && (
                        <>
                            <span>证件类型：{getIdcardType(record.idcardType) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>证件号：{record.idCard || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>国籍：{record.nationality || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生日期：{(!!record.birthday && dayjs(record.birthday).format('YYYY-MM-DD')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生地：{record.birthPlace || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>性别：{(record.memGender !== null && (Number(record.memGender) === 1 ? '男' : '女')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>银行账号：{record.bankCardNo || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>开户银行：{record.bankName || <Text type="secondary">&ndash;</Text>}</span>
                        </>
                    )}
                </>
            ),
        },
        {
            title: '修改记录',
            dataIndex: 'modifiedName',
            width: 150,
            render: (_, record) => {
                const formattedTime = record.createdAt ? dayjs(record.createdAt).format('YYYY-MM-DD HH:mm:ss') : '—';
                return (
                    <div>
                        {record.modifiedName || '—'}
                        {record.createdAt && (
                            <>
                                <br />
                                {formattedTime}
                            </>
                        )}
                    </div>
                );
            },
        },
    ];

    // 获取专家证件类型
    const getIdcardType = cardType => {
        const type = parseInt(cardType);
        if (type === 2) return '港澳居民来往内地通行证';
        if (type === 3) return '台湾居民来往大陆通行证';
        if (type === 4) return '外国人永久居留身份证';
        if (type === 5) return '护照';
        return null;
    };

    // 页码改变
    const onChangePage = (a, b) => {
        setPage(a);
        setPageSize(b);
    };

    // 处理金额
    const formatCurrency = value => {
        let result = '';

        const f = parseFloat(value) || 0.0;
        const parts = f.toFixed(2).split('.');
        result = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,') + (parts[1] ? '.' + parts[1] : '');

        return result;
    };

    // 劳务费列表
    const getLaborFeeExaminationList = async () => {
        setLoading(true);
        const res = await fetchLaborFeeExaminationList({
            page,
            pageSize,
            projectCode: projectCode,
            projectId: projectId,
            type: 1,
        });
        if (res && res.status_code === 200) {
            setData(res.data?.infos?.list || []);
            setTotal(res.data?.infos?.totalCount || 0);
            setStatisticalAmount(res.data?.totalInfo || {});
            // setLaborfeeStatus(res.data?.status);
        }
        setLoading(false);
    };

    // 含税劳务费请款审批结果查看
    const laborfeeResultCheck = async () => {
        const res = await fetchLaborFeeExaminationResult({ projectId });
        if (res && res.status_code === 200) {
            setPaymentRecord(res.data || null);
            setOpenPaymentRecord(true);
        }
        setOpenPaymentRecord(true);
    };

    // 下载审批结果
    const handleRecordOk = () => {
        setDownloading(true);
        setTimeout(() => {
            formHtmlToDownload('payment_record_wrap', () => setDownloading(false));
        }, 0);
    };

    const formHtmlToDownload = async (domId, callback) => {
        const res = await drawCanvas(domId);
        const linkA = document.createElement('a'); //创建a标签
        linkA.href = res; // 将图片的src赋值给a节点的href
        linkA.download = data.reportTitle || 'download'; // 设置a节点的download属性值(设置下载名称)
        const event = new MouseEvent('click'); // 模拟鼠标click点击事件
        linkA.dispatchEvent(event);
        callback();
    };

    const drawCanvas = async domId => {
        // 获取想要转换的 DOM 节点
        const dom = document.getElementById(domId);
        // const dom = document.querySelector('printHtml');
        const box = window.getComputedStyle(dom);

        // DOM 节点计算后宽高
        const width = parseValue(box.width);
        const height = parseValue(box.height);
        // 获取像素比-防止模糊
        const scaleBy = DPR();

        console.log(width, height, '======');

        // 创建自定义 canvas 元素
        const canvas = document.createElement('canvas');
        // 设定 canvas 元素属性宽高为 DOM 节点宽高 * 像素比
        canvas.width = width * scaleBy;
        // canvas.width = 1334
        canvas.height = height * scaleBy;
        // 设定 canvas css宽高为 DOM 节点宽高
        canvas.style.width = `${width}px`;
        // canvas.style.width = `1334px`;
        canvas.style.height = `${height}px`;
        // 获取画笔
        const context = canvas.getContext('2d');

        // 将所有绘制内容放大像素比倍
        context.scale(1, 1);

        // 将自定义 canvas 作为配置项传入，开始绘制
        return await html2canvas(dom, { canvas, useCORS: true }).then(canvas => {
            //document.querySelector("#canvasContainer").appendChild(canvas);
            //return canvas.toDataURL("image/png");
            return canvas.toDataURL('image/png');
        });
    };

    const DPR = () => {
        if (window.devicePixelRatio && window.devicePixelRatio > 1) {
            return window.devicePixelRatio;
        } else {
            return 1;
        }
    };

    const parseValue = value => {
        return parseInt(value, 10);
    };

    // 取消下载审批结果
    const handleRecordCancel = () => {
        setOpenPaymentRecord(false);
    };

    // 获取含税金额
    const getTotalAmount = (LaborFeeSum, IndividualTaxSum) => {
        if (LaborFeeSum !== null && IndividualTaxSum !== null) {
            return (Number(LaborFeeSum) + Number(IndividualTaxSum)).toFixed(2);
        }
    };

    // 获取会议信息
    const getConferenceInfo = async projectId => {
        const res = await conferenceOnceApi({ id: projectId });
        if (res.status_code === 200 && res.data) {
            setConference(res.data);
        }
    };

    // 获取劳务审批流程列表
    const getLaborFeeApprovalProcessList = async () => {
        const res = await fetchLaborFeeApprovalProcessList({
            conferenceId: projectId,
        });
        if (res.status_code === 200) {
            setApprovalProcessList(res.data || []);
        }
    };

    useEffect(() => {
        if (!!projectId) {
            getConferenceInfo(projectId);
        }
        requestAuthDict();
    }, []);

    useEffect(() => {
        getLaborFeeExaminationList();
        getLaborFeeApprovalProcessList();
    }, [page, pageSize, projectId]);

    return (
        <div className="page-container list-page-container">
            <PageHeader breadcrumb={[]}>
                <Link to={`/finance/laborfee/projects`} className="back">
                    <i className="bi bi-chevron-left"></i>
                </Link>
                <span className="current-title">含税劳务费总额审批</span>
                <span className="current-subtitle">{conference?.name}</span>
            </PageHeader>

            <Alert
                type="info"
                style={{ marginBottom: 16, fontSize: 12, flexShrink: 0 }}
                title={
                    <span style={{ fontSize: 12 }}>
                        实发劳务费总计：&yen;{formatCurrency(statisticalAmount?.LaborFeeSum)}元，相关税费总计：&yen;{formatCurrency(statisticalAmount?.IndividualTaxSum)}元，含税劳务费总计：&yen;{formatCurrency(getTotalAmount(statisticalAmount?.LaborFeeSum, statisticalAmount?.IndividualTaxSum))}元，共
                        {statisticalAmount?.realTotal}位专家
                    </span>
                }
            />

            <div ref={tableRegionRef} className="table-region-container">
                <div className="main-toolbar finance-laborfee-project-toolbar">
                    <div className="main-tools">
                        <Space>
                            {approvalButtonConfig.showApprovalBtn && (
                                <Button type="primary" onClick={laborfeeResultCheck}>
                                    劳务费审批
                                </Button>
                            )}
                        </Space>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            {approvalButtonConfig.showDetailBtn && (
                                <Button type="primary" onClick={laborfeeResultCheck}>
                                    审批详情
                                </Button>
                            )}
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="list-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        scroll={{ x: 'max-content', ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
                        pagination={{
                            total,
                            showTotal: () => `共${total}条`,
                            current: page,
                            pageSize: pageSize,
                            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                            showQuickJumper: true,
                            showSizeChanger: { placement: 'topRight' },
                            onChange: onChangePage,
                        }}
                    />
                </div>
            </div>

            <FinanceApprovalDetailModal
                open={openPaymentRecord}
                onClose={handleRecordCancel}
                paymentRecord={paymentRecord}
                projectId={projectId}
                statisticalAmount={statisticalAmount}
                modalColumns={modalColumns}
                modalLoading={modalLoading}
                listData={data}
                approvalProcessList={approvalProcessList}
                showAgreeBtn={approvalButtonConfig.showAgreeBtn}
                showDisagreeBtn={approvalButtonConfig.showDisagreeBtn}
                currentUserPhone={currentUserPhone}
                onRefresh={() => {
                    getLaborFeeExaminationList();
                    getLaborFeeApprovalProcessList();
                }}
            />
        </div>
    );
}
